import { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TabBar } from './TabBar';
import { InstallPrompt } from './InstallPrompt';
import { PushPermissionPrompt } from './PushPermissionPrompt';
import { SplashAnimation } from './SplashAnimation';
import { useTenant } from './TenantProvider';
import { useAttentionCount } from '@/hooks/useAttentionCount';

interface NavigatorWithAppBadge {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/**
 * AppShell — top-level layout for the PWA.
 *
 * 2026-05-03 R1 ChatGPT consult — fixed-viewport architecture.
 *
 * Rule: only ONE element scrolls per screen. AppShell is a fixed-height
 * flex column locked to the viewport. main provides height to the active
 * route via flex-1 + min-h-0 (NO overflow!) — each route owns its own
 * scroll. TabBar is a flex sibling at the bottom of the shell (NOT
 * position: fixed), so it doesn't fight the route's scroll context.
 *
 * Why: nested scroll containers + position:fixed children break sticky
 * positioning AND custom pull-to-refresh on iOS standalone PWAs (sticky
 * doesn't engage, pull gestures route to the wrong scroll layer).
 *
 * Safe-area handling moves into individual screens since they own scroll —
 * each route adds env(safe-area-inset-top) padding to its first row.
 */
export function AppShell() {
  const { slug } = useTenant();
  // OS app-icon badge mirrors the tab-dock Chats badge — both count
  // needs_attention so they always agree. While the app is OPEN this React
  // useEffect is the source of truth; while the app is CLOSED the service
  // worker takes over on each push (sw.js setAppBadge using badge_count from
  // the push payload). Reverted from useUnreadCount on 2026-05-23 — see
  // TabBar.tsx for the why.
  const attentionCount = useAttentionCount(slug);
  const location = useLocation();
  const navigate = useNavigate();
  // Tab-level key: animate when the user switches between top-level tabs.
  // Sub-routes within a tab (e.g. opening a conversation detail) don't
  // change tabKey, so navigating into a detail doesn't re-animate.
  const tabKey = location.pathname.split('/').slice(0, 3).join('/');

  // "Thread mode": when the route is /:slug/conversations/:id (a specific
  // conversation), hide the tab dock entirely. The dock is for navigating
  // between tabs; once you're inside a thread, the back button in the
  // conversation header is your way out, and the reply bar (position:fixed
  // bottom:0) needs the bottom of the screen for itself. iMessage / Slack
  // thread views do the same thing. Ernie 2026-05-22.
  const isConversationDetail = /^\/[^/]+\/conversations\/[^/]+/.test(
    location.pathname,
  );

  // Hide the bottom tab dock when an input is focused (keyboard is up).
  // Standard messaging-app UX — the dock slides out so the reply input has
  // clean space above the keyboard instead of competing with the dock.
  // Re-shows on blur. Ernie 2026-05-22.
  const [inputFocused, setInputFocused] = useState(false);

  // iOS standalone PWA viewport fitment — write the shell height to --app-h.
  //
  // The trap (diagnosed on-device via Safari Web Inspector 2026-05-21):
  // window.innerHeight, visualViewport.height, AND 100dvh all report the
  // SHORT height (e.g. 797 on a 844pt iPhone) — short by the bottom inset —
  // so sizing the shell off any of them leaves it ~47px above the physical
  // bottom and the tab dock floats with a gap beneath it. window.screen.height
  // is the only value that reflects the TRUE full height (844), and sizing the
  // shell off it drops the dock flush to the bottom edge (confirmed live).
  //
  // Keyboard guard: when the visual viewport shrinks well below the screen
  // height (software keyboard up), use the visual viewport instead so the
  // ConversationDetail reply bar stays above the keyboard rather than being
  // covered by it.
  useLayoutEffect(() => {
    // --app-h ALWAYS tracks screen.height. Don't try to shrink for the keyboard
    // here — the keyboard is the ReplyBar's problem, and it handles it by
    // switching to position:fixed bottom:0 when focused (iOS pins fixed
    // bottom-anchored elements to the visualViewport bottom, so the bar floats
    // above the keyboard naturally — the iMessage pattern).
    //
    // Why we tried and abandoned shell-shrink (2026-05-22): shrinking --app-h
    // by a fixed keyboard estimate stacked WITH iOS's own visualViewport shift,
    // double-counting the keyboard. The shell ended up half-height with dead
    // space between the input and the keyboard.
    //
    // We still TRACK focus here for one reason: hiding the bottom tab dock
    // when the keyboard is up (cleaner reply UX, standard messaging pattern).
    const isInputFocused = () => {
      const a = document.activeElement;
      return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA');
    };
    // Platform branch (Ernie 2026-05-25):
    //   - iOS standalone PWA: window.screen.height is the only value that
    //     reflects the TRUE full height (see big comment above). Keep using it.
    //   - Desktop / Android / non-standalone: window.screen.height is the FULL
    //     MONITOR (e.g. 1080) but the actual app window is smaller (e.g. 800),
    //     so sizing the shell off screen.height pushes the bottom tab dock
    //     OFF the visible window. Use window.innerHeight instead — which
    //     correctly tracks the app window on every non-iOS platform.
    const isIOSStandalone =
      typeof navigator !== 'undefined' &&
      (navigator as { standalone?: boolean }).standalone === true &&
      /iPad|iPhone|iPod/.test(navigator.userAgent);
    const writeAppHeight = () => {
      const h = isIOSStandalone
        ? (window.screen?.height || window.innerHeight)
        : window.innerHeight;
      document.documentElement.style.setProperty('--app-h', `${h}px`);
    };
    // Combined: re-write height AND update the focus-driven UI flag (used to
    // hide the bottom tab dock when typing). activeElement is read fresh on
    // every fire so focus moving between inputs doesn't race the listener
    // order. Cowork-greenlit pattern (2026-05-22).
    const onViewportChange = () => {
      writeAppHeight();
      setInputFocused(isInputFocused());
    };
    onViewportChange();
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
    window.addEventListener('pageshow', onViewportChange);
    window.addEventListener('focusin', onViewportChange);
    window.addEventListener('focusout', onViewportChange);
    // Desktop install: when Ernie resizes the PWA window on Mac, re-write
    // --app-h so the bottom tab dock follows. iOS standalone doesn't need
    // this (no resize handle), but it's harmless to keep universal.
    window.addEventListener('resize', onViewportChange);
    // Backup signal: capture-phase focus/blur listeners on document. iOS
    // standalone PWA has been observed to NOT fire focusin/focusout in some
    // cases (e.g. after a soft navigation when the input is mounted into a
    // re-keyed subtree), leaving --app-h at full screen and letting the
    // keyboard cover the reply input. Capture phase catches the raw event
    // before any sub-tree can stop it (Ernie 2026-05-22).
    document.addEventListener('focus', onViewportChange, true);
    document.addEventListener('blur', onViewportChange, true);
    return () => {
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
      window.removeEventListener('pageshow', onViewportChange);
      window.removeEventListener('focusin', onViewportChange);
      window.removeEventListener('focusout', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
      document.removeEventListener('focus', onViewportChange, true);
      document.removeEventListener('blur', onViewportChange, true);
    };
  }, []);

  // 2026-06-01 — "Open on Today when resumed after a break."
  //
  // Ernie's iPhone PWA was retaining its previous URL on resume. iOS keeps
  // the PWA in memory for a while after backgrounding and brings it back to
  // wherever you left it — so tapping the home-screen icon after a few minutes
  // of doing other things drops you into a stale conversation detail page
  // instead of Today. The manifest's start_url='/' only takes effect on a
  // true cold start (after iOS evicts the PWA).
  //
  // Fix: listen for visibilitychange. When the PWA becomes visible again
  // after being hidden for more than the threshold, navigate to Today. Brief
  // context switches (<30s, e.g. tapping over to look something up) stay on
  // the current page so the user isn't yanked out mid-reply. Longer breaks
  // are treated as fresh sessions.
  useEffect(() => {
    const HIDDEN_RESUME_THRESHOLD_MS = 30_000;
    let hiddenAt: number | null = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt != null) {
        const elapsed = Date.now() - hiddenAt;
        hiddenAt = null;
        if (elapsed > HIDDEN_RESUME_THRESHOLD_MS) {
          navigate(`/${slug}/today`, { replace: true });
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [slug, navigate]);

  // OS-level app icon badge (iOS 16.4+ standalone, modern Android Chrome).
  useEffect(() => {
    const nav = navigator as unknown as NavigatorWithAppBadge;
    if (typeof nav.setAppBadge !== 'function') return;
    if (attentionCount > 0) {
      nav.setAppBadge(attentionCount).catch(() => {
        /* ignore — non-critical */
      });
    } else if (typeof nav.clearAppBadge === 'function') {
      nav.clearAppBadge().catch(() => {
        /* ignore */
      });
    }
  }, [attentionCount]);

  return (
    <div className="flex h-full w-full max-w-full flex-col overflow-hidden bg-ink-50 dark:bg-[#0B1220] [--tab-h:64px]">
      {/* SplashAnimation renders fixed inset-0 z-50; self-unmounts after
          ~2.95s on cold load. */}
      <SplashAnimation />

      {/* main = layout container only. NO overflow-y-auto. min-h-0 is
          required so flex-1 children can shrink + scroll properly inside
          a flex column. The h-full key on the tab wrapper propagates
          height to the active route. */}
      <main className="flex min-h-0 flex-1 flex-col">
        <div key={tabKey} className="flex h-full min-h-0 flex-1 flex-col animate-ss-fade-opacity">
          <Outlet />
        </div>
      </main>

      {/* Bottom tab bar — flex sibling (NOT fixed) so it sits at the
          bottom of the viewport via layout, not positioning. Won't
          interfere with the active route's scroll context. Hidden when
          an input is focused (keyboard up) so the reply bar has clean
          space above the keyboard. Ernie 2026-05-22. */}
      {!inputFocused && !isConversationDetail && <TabBar />}

      {/* Install + push prompts — both render fixed inset-x-X bottom
          overlays on top of everything when active. */}
      <InstallPrompt />
      <PushPermissionPrompt slug={slug} />
    </div>
  );
}
